/**
 * ASCII Art Generator - Beam 光束效果
 *
 * 单条光线横穿画面，周期性重复扫描
 */

import type { BeamConfig } from './types'
import { getDirectionVector } from './helpers'

// ============================================
// Canvas 应用函数
// ============================================

/**
 * 应用光束效果到 Canvas
 * @param canvas 目标画布
 * @param config 光束配置
 */
export function applyBeam(
  canvas: HTMLCanvasElement,
  config: BeamConfig
): void {
  const ctx = canvas.getContext('2d')!
  const { width, height } = canvas
  const { strength, width: beamWidth, speed, direction, color, time } = config

  // 获取方向向量
  const dirVector = getDirectionVector(direction)

  // 光束位置（基于时间动画）
  const period = Math.max(width, height) + beamWidth * 2
  const beamPosition = (time * speed * 100) % period - beamWidth

  // 使用 lighter 混合模式增加亮度
  ctx.globalCompositeOperation = 'lighter'

  // 解析光束颜色
  const rgb = parseColor(color)

  // 创建光束渐变
  if (Math.abs(dirVector.dx) >= Math.abs(dirVector.dy)) {
    // 水平方向光束
    const gradient = ctx.createLinearGradient(
      beamPosition - beamWidth, 0,
      beamPosition + beamWidth, 0
    )

    // 提高强度系数使光束更明显
    const alpha = strength * 0.6
    gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`)
    gradient.addColorStop(0.3, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.5})`)
    gradient.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`)
    gradient.addColorStop(0.7, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.5})`)
    gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`)

    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
  } else {
    // 垂直方向光束
    const gradient = ctx.createLinearGradient(
      0, beamPosition - beamWidth,
      0, beamPosition + beamWidth
    )

    // 提高强度系数使光束更明显
    const alpha = strength * 0.6
    gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`)
    gradient.addColorStop(0.3, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.5})`)
    gradient.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`)
    gradient.addColorStop(0.7, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.5})`)
    gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`)

    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
  }

  // 恢复默认混合模式
  ctx.globalCompositeOperation = 'source-over'
}

// ============================================
// 颜色解析
// ============================================

/**
 * 解析颜色字符串为 RGB
 */
function parseColor(color: string): { r: number; g: number; b: number } {
  // 处理 hex 颜色
  if (color.startsWith('#')) {
    const hex = color.slice(1)
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0] + hex[0], 16),
        g: parseInt(hex[1] + hex[1], 16),
        b: parseInt(hex[2] + hex[2], 16)
      }
    } else if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16)
      }
    }
  }

  // 默认白色
  return { r: 255, g: 255, b: 255 }
}

// ============================================
// 类封装（用于动画）
// ============================================

/**
 * 光束效果类
 * 用于管理光束状态和动画
 */
export class BeamEffect {
  private config: BeamConfig
  private time: number = 0

  constructor(config: BeamConfig) {
    this.config = config
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 更新配置
   */
  updateConfig(config: BeamConfig): void {
    this.config = config
  }

  /**
   * 应用效果
   */
  apply(canvas: HTMLCanvasElement): void {
    const config = { ...this.config, time: this.time }
    applyBeam(canvas, config)
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

// ============================================
// 配置工厂函数
// ============================================

/**
 * 从用户设置创建光束配置
 */
export function createBeamConfigFromSettings(
  strength: number,
  width: number,
  speed: number,
  direction: string,
  color: string
): BeamConfig {
  return {
    strength: Math.max(0, Math.min(1, strength)),
    width: Math.max(10, Math.min(100, Math.round(width))),
    speed: Math.max(0.1, Math.min(10, speed)),
    direction: direction as any,
    color,
    time: 0
  }
}

// ============================================
// 默认配置
// ============================================

/** 默认光束配置 - 参数已调整使效果更明显 */
export const DEFAULT_BEAM_CONFIG: BeamConfig = {
  strength: 0.7,
  width: 50,
  speed: 1.5,
  direction: 'right',
  color: '#ffffff',
  time: 0
}